import React, { useState, useRef, useEffect, useContext } from "react";
import styled, { css } from "styled-components";
import Review from "./Review";
import { GREEN } from "../styles/constants";

import Loader from "../common/Loader";
import { Query } from "react-apollo";
import getUndef from "../../lib/get-undef";

import { GET_REVIEWS } from "../../queries/Reviews";

import { CityContext } from "../context/CityContext";
import { gerReviews } from "../../__generated__/gerReviews";

interface Review {
  name: string;
  date: string;
  text: string;
  type: string;
  answer?: string;
}

const ReviewsList = ({ appraisal }) => {
  const { domain } = useContext(CityContext);
  const [howMuchSkip, setHowMuchSkip] = useState(10);
  const offset = 10;

  const reviewsListRef = useRef(null);
  const loadMore = useRef(null);
  const hasNextPageRef = useRef(null);

  useEffect(() => {
    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        entry.isIntersecting && hasNextPageRef.current && loadMore.current
          ? loadMore.current()
          : null;
      });
    };

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    };

    const observer = new IntersectionObserver(handleIntersection, options);
    observer.observe(reviewsListRef.current);

    return () => observer.disconnect();
  }, [reviewsListRef, loadMore, hasNextPageRef]);

  const defaultWhere = {
    shop: { primaryDomain: { host: domain } },
  };

  const where = appraisal
    ? {
        ...defaultWhere,
        type: appraisal,
      }
    : defaultWhere;

  return (
    <List>
      <Query<gerReviews>
        query={GET_REVIEWS}
        variables={{
          where,
          skip: 0,
          first: 10,
        }}
        notifyOnNetworkStatusChange={true}
        fetchPolicy="network-only"
      >
        {({ loading, error, data, fetchMore }: any) => {
          if (error || !getUndef(() => data.paginate.reviews.edges)) {
            return null;
          }

          const loadMoreReviews = () => {
            fetchMore({
              variables: {
                skip: howMuchSkip,
              },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) return previousResult;

                const previousPaginate = previousResult.paginate || {};
                const currentPaginate = fetchMoreResult.paginate || {};
                const previousReviews = previousPaginate.reviews || {};
                const currentReviews = currentPaginate.reviews || {};
                const previousEdges = previousReviews.edges || [];
                const currentEdges = currentReviews.edges || [];
                const currentPageInfo = currentReviews.pageInfo || {};
                const previousAggregate = previousReviews.aggregate || {};

                return Object.assign({}, previousResult, {
                  paginate: {
                    ...previousPaginate,
                    reviews: {
                      aggregate: {
                        ...previousAggregate,
                        count: previousAggregate.count + offset,
                      },
                      edges: [...previousEdges, ...currentEdges],
                      pageInfo: {
                        ...currentPageInfo,
                      },
                      __typename: "ReviewConnection",
                    },
                  },
                });
              },
            });

            setHowMuchSkip((howMuchSkip) => howMuchSkip + offset);
          };

          loadMore.current = loadMoreReviews;
          hasNextPageRef.current = data.paginate.reviews.pageInfo.hasNextPage;

          const reviewList = data.paginate.reviews.edges;

          return (
            <section>
              {reviewList.map((item, ind) => {
                const { node } = item;
                const { answer } = node;

                return (
                  <ItemWrap key={ind}>
                    <Item answer={answer} listItem={node} />
                    {answer && (
                      <Answer>
                        <AnswerName>Ответ Администратора</AnswerName>
                        <p>{answer}</p>
                      </Answer>
                    )}
                  </ItemWrap>
                );
              })}

              {loading && (
                <LoaderWrapper>
                  <Loader />
                </LoaderWrapper>
              )}
            </section>
          );
        }}
      </Query>
      <Trigger ref={reviewsListRef} />
    </List>
  );
};

const ItemWrap = styled.li`
  position: relative;
  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

interface ItemProps {
  answer?: string;
}
const paddingStyle = css`
  & {
    padding-bottom: 60px;
  }
`;
const Item = styled(Review)<ItemProps>`
  ${({ answer }) => (answer ? paddingStyle : undefined)};
`;

const Answer = styled.div`
  margin: -40px 20px 0 60px;
  border-radius: 18px;
  background-color: ${GREEN};
  color: #fff;
  padding: 30px;
  box-shadow: 0px 12px 22px rgba(0, 0, 0, 0.1);

  @media (max-width: 600px) {
    margin-left: 0;
    margin-right: 0;
  }

  @media (max-width: 450px) {
    padding: 20px;
  }
`;

const AnswerName = styled.p`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
`;

const List = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
`;

const Trigger = styled.div`
  height: 100px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
`;

export default ReviewsList;
